// Google Analytics utility functions

const MEASUREMENT_ID = 'G-RLTLWN2F1D';
const isDevelopment = process.env.NODE_ENV === 'development';
let isGALoaded = false;
let gaInjectStarted = false;
/** True once analytics consent granted and gtag stub is installed. */
let gaLoadStarted = false;

const pendingCalls = [];
const readyListeners = new Set();

function isGtagReady() {
    return typeof window !== 'undefined' && typeof window.gtag === 'function';
}

function flushPendingCalls() {
    if (!isGtagReady()) return;
    while (pendingCalls.length > 0) {
        const call = pendingCalls.shift();
        try {
            call();
        } catch (err) {
            console.error('[GoogleAnalytics] flush failed:', err);
        }
    }
}

function runWhenGtagReady(fn) {
    if (isGtagReady()) {
        fn();
        return;
    }
    pendingCalls.push(fn);
}

/** Subscribe to analytics becoming ready (consent + gtag stub). Returns unsubscribe. */
export function onAnalyticsReady(listener) {
    if (typeof listener !== 'function') {
        return () => {};
    }
    if (gaLoadStarted && isGtagReady()) {
        listener();
    }
    readyListeners.add(listener);
    return () => readyListeners.delete(listener);
}

function notifyReadyListeners() {
    if (!gaLoadStarted || !isGtagReady()) return;
    readyListeners.forEach((listener) => {
        try {
            listener();
        } catch (err) {
            console.error('[GoogleAnalytics] ready listener failed:', err);
        }
    });
}

/**
 * Load Google Analytics script dynamically (only in production)
 */
const loadGoogleAnalytics = () => {
    if (isDevelopment || isGALoaded || gaInjectStarted || typeof window === 'undefined') {
        return;
    }
    gaInjectStarted = true;
    gaLoadStarted = true;

    // Initialize dataLayer
    window.dataLayer = window.dataLayer || [];
    function gtag() {
        window.dataLayer.push(arguments);
    }
    window.gtag = gtag;
    gtag('js', new Date());

    flushPendingCalls();
    notifyReadyListeners();

    // Load the Google Analytics script
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${MEASUREMENT_ID}`;
    document.head.appendChild(script);

    // Initialize GA after script loads
    script.onload = () => {
        gtag('config', MEASUREMENT_ID);
        isGALoaded = true;
        flushPendingCalls();
        notifyReadyListeners();
    };
};

/**
 * Initialize Google Analytics (production web only).
 * Loads only after explicit analytics consent via Law 25 banner (`window.PIConsent`).
 */
export const initGA = () => {
    if (isDevelopment) {
        console.log('Google Analytics disabled in development mode');
        return;
    }
    if (typeof window === 'undefined') {
        return;
    }
    const PI = window.PIConsent;
    if (!PI || typeof PI.onConsent !== 'function') {
        console.warn('[GoogleAnalytics] PIConsent not available; analytics will not load.');
        return;
    }
    PI.onConsent('analytics', loadGoogleAnalytics);

    window.addEventListener('pi-consent-update', (event) => {
        if (event?.detail?.analytics) {
            loadGoogleAnalytics();
        }
    });
};

/** Normalize pathname for route matching (strip trailing slash). */
function normalizePathname(pathname) {
    if (typeof pathname !== 'string' || !pathname) return '/';
    const trimmed = pathname.replace(/\/+$/, '');
    return trimmed || '/';
}

/**
 * Puzzle context from public play/browse routes (for GA route + entered events).
 * @param {string} pathname
 * @returns {Record<string, string>|null}
 */
export function getPuzzleRouteContext(pathname) {
    const path = normalizePathname(pathname);

    if (path === '/puzzles/jigsaw') {
        return { puzzle_type: 'JIGSAW', page_kind: 'browse' };
    }
    if (path === '/puzzles/sliding') {
        return { puzzle_type: 'SLIDING', page_kind: 'browse' };
    }
    if (path === '/storybook') {
        return { page_type: 'STORYBOOK', page_kind: 'browse' };
    }
    if (path === '/dashboard') {
        return { page_type: 'DASHBOARD', page_kind: 'account' };
    }
    if (path === '/word-of-day') {
        return { page_type: 'WORD_OF_DAY', page_kind: 'browse' };
    }
    if (path === '/puzzle-of-day' || path.startsWith('/puzzle-of-day/')) {
        return { page_type: 'PUZZLE_OF_DAY', page_kind: 'browse' };
    }

    const jigsawBoard = path.match(/^\/puzzle\/play-jigsaw\/([^/]+)\/board\/([^/]+)/);
    if (jigsawBoard) {
        return {
            puzzle_type: 'JIGSAW',
            page_kind: 'play',
            story_id: jigsawBoard[1],
            puzzle_id: jigsawBoard[2],
        };
    }

    const jigsawSelect = path.match(/^\/puzzle\/play-jigsaw\/([^/]+)/);
    if (jigsawSelect) {
        return {
            puzzle_type: 'JIGSAW',
            page_kind: 'select',
            story_id: jigsawSelect[1],
        };
    }

    const slidingBoard = path.match(/^\/puzzle\/play-sliding\/([^/]+)\/board\/([^/]+)/);
    if (slidingBoard) {
        return {
            puzzle_type: 'SLIDING',
            page_kind: 'play',
            story_id: slidingBoard[1],
            puzzle_id: slidingBoard[2],
        };
    }

    const slidingSelect = path.match(/^\/puzzle\/play-sliding\/([^/]+)/);
    if (slidingSelect) {
        return {
            puzzle_type: 'SLIDING',
            page_kind: 'select',
            story_id: slidingSelect[1],
        };
    }

    return null;
}

/** GA4 event name for puzzle type — visible in Events report without custom dimensions. */
export function getPuzzleTrafficEventName(puzzleType) {
    if (puzzleType === 'JIGSAW') return 'jigsaw_traffic';
    if (puzzleType === 'SLIDING') return 'sliding_traffic';
    return null;
}

/**
 * Track a page view
 * @param {string} path - The path of the page
 * @param {string} title - Optional page title
 */
export const trackPageView = (path, title = null) => {
    if (isDevelopment || !gaLoadStarted) return;
    runWhenGtagReady(() => {
        window.gtag('config', MEASUREMENT_ID, {
            page_path: path,
            page_title: title || document.title,
        });
    });
};

/**
 * Track a custom event
 * @param {string} eventName - The name of the event
 * @param {object} eventParams - Additional parameters for the event
 */
export const trackEvent = (eventName, eventParams = {}) => {
    if (isDevelopment || !gaLoadStarted) return;
    runWhenGtagReady(() => {
        window.gtag('event', eventName, eventParams);
    });
};

/**
 * Set user properties
 * @param {object} properties - User properties to set
 */
export const setUserProperties = (properties) => {
    if (isDevelopment || !gaLoadStarted) return;
    runWhenGtagReady(() => {
        window.gtag('set', 'user_properties', properties);
    });
};

/**
 * Set user ID for tracking
 * @param {string} userId - The user ID
 */
export const setUserId = (userId) => {
    if (isDevelopment || !gaLoadStarted) return;
    runWhenGtagReady(() => {
        window.gtag('config', MEASUREMENT_ID, {
            user_id: userId,
        });
    });
};
